import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertCircle, Camera, Paperclip, Pencil, Upload, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { REPORT_IMAGE_LIMITS, addImageFiles, overflowMessage } from '../lib/reportImages';

const SOURCE = {
    capture: { label: 'Captured from this page', Icon: Camera },
    upload: { label: 'Attached from your device', Icon: Paperclip },
    annotated: { label: 'Annotated', Icon: Pencil },
};

function Tip({ label, children }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side="bottom" className="bg-stone-900 text-white text-[11px] px-2 py-1">{label}</TooltipContent>
        </Tooltip>
    );
}

/**
 * The image area of the report dialog (#932): thumbnails with Annotate and
 * Remove, Attach image (picker or drop), Capture page, the `n of 5` counter
 * and a message for every file that was refused.
 *
 * Controlled: the dialog owns `images` ([{id, dataUrl, source, name?}]) and
 * `onChange` is its state setter: it is called with an updater, so a change
 * applies to the list as it is then. Capture and Annotate are the dialog's job
 * too, because the dialog has to step aside for both.
 */
export default function ReportImageList({
    images,
    onChange,
    onCapture,
    onAnnotate,
    captureError = null,
    capturing = false,
    limits = REPORT_IMAGE_LIMITS,
}) {
    const inputRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [reading, setReading] = useState(0);
    const full = images.length >= limits.maxCount;
    const uid = useId();
    const labelId = `${uid}-label`;
    const captureErrorId = `${uid}-capture-error`;
    // The list as last rendered, for a read that finishes after it changed.
    const latest = useRef(images);
    useEffect(() => { latest.current = images; }, [images]);

    const addFiles = async (files) => {
        if (!files || !files.length) return;
        // Capture waits while files are read, so it can't land in between.
        setReading((n) => n + 1);
        try {
            const { added, messages: refused, overflow } = await addImageFiles(files, images.length, limits);
            // Applied to the list as it is now, not as it was when the read began:
            // a remove or another pick can land meanwhile. What no longer fits is
            // counted in the "not added" line.
            const room = Math.max(0, limits.maxCount - latest.current.length);
            const late = Math.max(0, added.length - room);
            setMessages(late
                ? [...refused.filter((m) => m !== overflowMessage(overflow, limits)), overflowMessage(overflow + late, limits)]
                : refused);
            if (added.length) onChange((current) => [...current, ...added].slice(0, limits.maxCount));
        } catch (err) {
            console.error('Reading an attached image failed:', err);
            setMessages(["A file couldn't be read. Try attaching it again."]);
        } finally {
            setReading((n) => n - 1);
        }
    };

    const remove = (id) => {
        setMessages([]);
        onChange((current) => current.filter((img) => img.id !== id));
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer?.files);
    };

    return (
        <TooltipProvider delayDuration={150}>
            <section
                aria-labelledby={labelId}
                data-testid="report-images"
                className={`space-y-2 rounded-lg ${dragging ? 'outline outline-2 outline-dashed outline-offset-4 outline-blue-500' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
                onDrop={onDrop}
            >
                <div className="flex items-center justify-between">
                    <span id={labelId} className="text-xs font-medium text-stone-600">Screenshots</span>
                    <span
                        data-testid="report-images-count"
                        className={`text-[11px] font-medium tabular-nums ${full ? 'text-stone-700 bg-stone-100 px-2 rounded-full' : 'text-stone-500'}`}
                    >
                        {images.length} of {limits.maxCount}{full ? ' · limit reached' : ''}
                    </span>
                </div>

                {captureError && (
                    <div role="status" id={captureErrorId} data-testid="report-capture-error"
                        className="flex gap-2.5 items-start px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-snug">
                        <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-px" aria-hidden="true" />
                        <span><strong className="font-semibold">Couldn&apos;t capture this page:</strong> {captureError}. You can attach an image instead, or try Capture page again.</span>
                    </div>
                )}

                {images.length > 0 && (
                    <ul className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(136px,1fr))]">
                        {images.map((img, i) => {
                            const src = SOURCE[img.source] || SOURCE.upload;
                            return (
                                <li key={img.id} className="group relative min-w-0" data-testid="report-image">
                                    <div className="aspect-[16/10] w-full rounded-lg border border-stone-200 overflow-hidden bg-stone-50 transition-colors group-hover:border-emerald-600 group-hover:ring-1 group-hover:ring-emerald-600 group-focus-within:border-emerald-600 group-focus-within:ring-1 group-focus-within:ring-emerald-600">
                                        <img src={img.dataUrl} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" />
                                    </div>
                                    <Tip label={src.label}>
                                        <span role="img" aria-label={src.label}
                                            className="absolute left-1.5 bottom-1.5 w-6 h-6 rounded-full bg-stone-900/80 text-white flex items-center justify-center">
                                            <src.Icon className="w-3.5 h-3.5" aria-hidden="true" />
                                        </span>
                                    </Tip>
                                    <div className="absolute right-1.5 top-1.5 flex gap-1">
                                        <Tip label="Annotate">
                                            <button type="button" onClick={() => onAnnotate(img.id)}
                                                aria-label={`Annotate image ${i + 1}`} data-annotate-id={img.id}
                                                className="w-7 h-7 rounded-full bg-white/95 shadow text-stone-700 hover:text-stone-900 hover:bg-white flex items-center justify-center">
                                                <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                                            </button>
                                        </Tip>
                                        <Tip label="Remove">
                                            <button type="button" onClick={() => remove(img.id)}
                                                aria-label={`Remove image ${i + 1}`}
                                                className="w-7 h-7 rounded-full bg-white/95 shadow text-stone-700 hover:text-red-700 hover:bg-white flex items-center justify-center">
                                                <X className="w-3.5 h-3.5" aria-hidden="true" />
                                            </button>
                                        </Tip>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <div aria-live="polite" data-testid="report-image-messages">
                    {messages.length > 0 && (
                        <div className="flex flex-col gap-1.5 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs leading-snug">
                            {messages.map((m, i) => (
                                // eslint-disable-next-line react/no-array-index-key -- two refused files can share a message
                                <div key={`${i}-${m}`} className="flex gap-2 items-start">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                                    <span>{m}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-2">
                    <button type="button" onClick={() => inputRef.current?.click()} disabled={full}
                        aria-label={full ? 'Attach image (limit reached)' : 'Attach image (PNG or JPEG, up to 5 MB)'}
                        title={full ? `Up to ${limits.maxCount} images` : undefined}
                        data-testid="report-attach"
                        className="flex-1 min-w-0 h-10 px-3 flex items-center gap-2 rounded-lg border-[1.5px] border-dashed border-stone-300 bg-stone-50 text-stone-700 text-[13px] font-medium whitespace-nowrap overflow-hidden hover:bg-stone-100 hover:border-stone-400 disabled:cursor-not-allowed disabled:text-stone-400 disabled:border-stone-200 disabled:hover:bg-stone-50">
                        <Upload className="w-4 h-4 shrink-0" aria-hidden="true" />
                        <span>{full ? 'Attach image · remove one to add more' : 'Attach image'}</span>
                        {!full && <span className="hidden sm:inline font-normal text-[11.5px] text-stone-500 truncate">or drop PNG / JPEG · max 5 MB each</span>}
                    </button>
                    <button type="button" onClick={onCapture} disabled={full || capturing || reading > 0}
                        title={full ? `Up to ${limits.maxCount} images` : undefined}
                        aria-describedby={captureError ? captureErrorId : undefined}
                        data-testid="report-capture"
                        className="shrink-0 h-10 px-3 flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white text-stone-900 text-[13px] font-medium whitespace-nowrap hover:bg-stone-50 disabled:cursor-not-allowed disabled:text-stone-400 disabled:bg-stone-50">
                        <Camera className="w-4 h-4" aria-hidden="true" />
                        Capture page
                    </button>
                </div>
                <input ref={inputRef} type="file" accept="image/png,image/jpeg" multiple hidden
                    data-testid="report-file-input"
                    onChange={(e) => { const picked = Array.from(e.target.files || []); e.target.value = ''; addFiles(picked); }} />
            </section>
        </TooltipProvider>
    );
}
